import { useState, useEffect, useRef, useCallback } from 'react'
import { Button, Card, Flex, Text } from '@radix-ui/themes'
import { XIcon } from 'lucide-react'

import {
  buildAutoFinUrl,
  defaultAutoFinConfig,
  mapVehicleToAutoFin,
  mapClientToAutoFin,
  AutoFinConfig,
  AutoFinCreditParams,
} from '../utils/autofin'

interface AutoFinIframeProps {
  vehicle: any
  client?: any
  businessType?: 1 | 2
  customPrice?: number
  idExterno?: string
  config?: Partial<AutoFinConfig>
  isOpen: boolean
  onClose: () => void
  onLoad?: () => void
  onError?: (error: string) => void
}

/**
 * AutoFin Araña iframe component for credit evaluation
 * Provides embedded credit evaluation interface from AutoFin
 */
const AutoFinIframe: React.FC<AutoFinIframeProps> = ({
  vehicle,
  client,
  businessType = 2, // Default to Convencional
  customPrice,
  idExterno,
  config = {},
  isOpen,
  onClose,
  onLoad,
  onError,
}) => {
  console.log('AutoFinIframe component rendered with props:', {
    isOpen,
    hasVehicle: !!vehicle,
    hasClient: !!client,
    businessType,
    customPrice,
    idExterno,
  })

  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [iframeUrl, setIframeUrl] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string>('')

  const handleIframeLoad = useCallback(() => {
    console.log('🎉 Iframe onLoad fired!')
    setIsLoading(false)
    onLoad?.()
  }, [onLoad])

  const handleIframeError = useCallback(() => {
    console.log('❌ Iframe onError fired!')
    setIsLoading(false)
    const errorMessage = 'Error al cargar la interfaz de AutoFin'
    setError(errorMessage)
    onError?.(errorMessage)
  }, [onError])

  // Attach event listeners directly to iframe
  useEffect(() => {
    const iframe = iframeRef.current

    if (!iframe || !iframeUrl) return

    console.log('🔗 Attaching load/error listeners to iframe')

    // Direct event listeners
    iframe.addEventListener('load', handleIframeLoad)
    iframe.addEventListener('error', handleIframeError)

    // Fallback timeout - show iframe after 5 seconds even if onLoad doesn't fire
    const fallbackTimeout = setTimeout(() => {
      console.log('⏰ Fallback timeout triggered - showing iframe')
      setIsLoading(false)
    }, 5000)

    return () => {
      iframe.removeEventListener('load', handleIframeLoad)
      iframe.removeEventListener('error', handleIframeError)
      clearTimeout(fallbackTimeout)
    }
  }, [iframeUrl, handleIframeLoad, handleIframeError])

  // Only set loading to true when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsLoading(true)
      setError('')

      // Quick fallback - hide loading after 500ms
      const quickFallback = setTimeout(() => {
        console.log('⚡ Quick fallback - hiding loading state')
        setIsLoading(false)
      }, 500)

      return () => clearTimeout(quickFallback)
    }
  }, [isOpen])

  // Build iframe URL
  useEffect(() => {
    if (!isOpen) return

    const buildUrl = async () => {
      try {
        console.log('AutoFin useEffect triggered with:', {
          isOpen,
          vehicle: !!vehicle,
          client: !!client,
          businessType,
          idExterno,
          config: Object.keys(config),
        })

        // Merge config with defaults
        const finalConfig: AutoFinConfig = {
          ...defaultAutoFinConfig,
          ...config,
        }

        console.log('Final AutoFin config:', finalConfig)

        // Map vehicle and client data (now async)
        const vehicleData = await mapVehicleToAutoFin(vehicle, idExterno)
        const clientData = client ? mapClientToAutoFin(client) : undefined

        // Apply custom price override if provided
        const finalVehicleData = customPrice !== undefined ? { ...vehicleData, price: customPrice } : vehicleData

        console.log('Mapped vehicle data:', vehicleData)
        console.log('Final vehicle data (with custom price):', finalVehicleData)
        console.log('Mapped client data:', clientData)

        // Build iframe URL
        const creditParams: AutoFinCreditParams = {
          businessType,
          idExterno,
        }

        const url = buildAutoFinUrl(finalConfig, finalVehicleData, clientData, creditParams)
        console.log('AutoFin URL generated:', url)
        setIframeUrl(url)

        // Validate URL
        if (!url.startsWith('https://')) {
          throw new Error('Invalid AutoFin URL generated')
        }
      } catch (err) {
        console.error('Error in AutoFin useEffect:', err)
        const errorMessage = err instanceof Error ? err.message : 'Error al generar URL de AutoFin'
        setError(errorMessage)
        onError?.(errorMessage)
        setIsLoading(false)
      }
    }

    buildUrl()
  }, [vehicle, client, businessType, customPrice, idExterno, config, onError, isOpen])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-6xl h-[90vh] flex flex-col">
        {/* Header */}
        <Flex justify="between" align="center" p="4" className="border-b">
          <Text size="5" weight="bold">
            Evaluación de Crédito AutoFin
          </Text>
          <Button variant="ghost" size="2" onClick={onClose}>
            <XIcon size={16} />
          </Button>
        </Flex>

        {/* Content */}
        <div className="flex-1 relative">
          {/* Render iframe immediately */}
          {!error && iframeUrl && (
            <>
              <iframe
                ref={iframeRef}
                src={iframeUrl}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  borderRadius: '0 0 8px 8px',
                  visibility: isLoading ? 'hidden' : 'visible',
                }}
                title="AutoFin Credit Evaluation"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; camera; microphone"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />

              {/* Fallback link if iframe doesn't load */}
              {!isLoading && (
                <div className="absolute bottom-4 right-4 bg-white bg-opacity-90 p-2 rounded-lg shadow-lg border">
                  <Text size="1" color="gray">
                    ¿No se carga?{' '}
                  </Text>
                  <a
                    href={iframeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline text-sm"
                  >
                    Abrir en nueva pestaña
                  </a>
                </div>
              )}
            </>
          )}

          {/* Loading overlay */}
          {isLoading && !error && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                <Text>Cargando interfaz de AutoFin...</Text>
              </div>
            </div>
          )}

          {/* Error overlay */}
          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-red-50 z-10">
              <div className="text-center text-red-600">
                <Text size="4" weight="bold">
                  Error
                </Text>
                <Text>{error}</Text>
                <Button mt="2" onClick={onClose} variant="outline">
                  Cerrar
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <Flex justify="end" p="4" className="border-t bg-gray-50">
          <Text size="2" color="gray">
            Powered by AutoFin - Evaluación de crédito en tiempo real
          </Text>
        </Flex>
      </Card>
    </div>
  )
}

export default AutoFinIframe
